import { useMemo } from 'react';

import type { ColumnDef, TableOptions } from './useTable/types.js';
import { ROW_DETAILS_COLUMN } from '../features/RowDetails/RowDetails.js';
import { ROW_DRAG_COLUMN } from '../features/RowOrder/RowOrder.js';
import { ROW_SELECTION_COLUMN } from '../features/RowSelection/RowSelection.js';
import { ROW_ACTIONS_COLUMN } from '../features/UserActions/RowActions.js';

/**
 * Returns the user-defined `options.columns` enriched by built-in prefix and suffix columns
 * (depending on which features are enabled).
 * @param options - the table options containing the user-defined columns
 * and flags for enabled/disabled features.
 * @internal
 */
export const useBuiltInColumns = <TData>(options: TableOptions<TData>) => {
  return useMemo(() => {
    const cols = [...options.columns];

    const rowDetailsColumn = ROW_DETAILS_COLUMN as ColumnDef<TData, unknown>;
    if (options.enableRowDetails === true && !cols.includes(rowDetailsColumn)) {
      cols.unshift(rowDetailsColumn);
    }

    const rowSelectionColumn = ROW_SELECTION_COLUMN as ColumnDef<
      TData,
      unknown
    >;
    if (
      options.enableRowSelection !== false &&
      !cols.includes(rowSelectionColumn)
    ) {
      cols.unshift(rowSelectionColumn);
    }

    const rowDragColumn = ROW_DRAG_COLUMN as ColumnDef<TData, unknown>;
    if (
      options.enableRowOrderDragAndDrop !== false &&
      !cols.includes(rowDragColumn)
    ) {
      cols.unshift(rowDragColumn);
    }

    const rowActionsColumn = ROW_ACTIONS_COLUMN as ColumnDef<TData, unknown>;
    if (options.enableRowActions === true && !cols.includes(rowActionsColumn)) {
      const spacerIndex = cols.findIndex(
        (column) => column.meta?.spacer === true,
      );
      cols.splice(spacerIndex, 0, rowActionsColumn);
    }

    return cols;
  }, [
    options.columns,
    options.enableRowOrderDragAndDrop,
    options.enableRowDetails,
    options.enableRowSelection,
    options.enableRowActions,
  ]);
};
